import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import {
	AnimatePresence,
	domMax,
	LazyMotion,
	m,
	type Transition,
} from "motion/react";
import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";
import { Icon } from "../icon";
import { Ripple, useRippleState } from "../ripple";
import {
	SPRING_TRANSITION,
	SPRING_TRANSITION_EXPRESSIVE,
} from "../shared/constants";
import { TouchTarget } from "../shared/touch-target";
import {
	getNavigationShapeStyle,
	isSquareShape,
	type NavigationItemShape,
} from "./navigation-shape-utils";

export type { NavigationItemShape };

// ─────────────────────────────────────────────────────────────────────────────
// Types & Constants
// ─────────────────────────────────────────────────────────────────────────────

export type NavigationRailVariant = "collapsed" | "expanded" | "modal" | "xr";
export type NavigationRailLabelVisibility = "labeled" | "auto" | "unlabeled";
export type NavigationRailActiveIndicatorWidth = "hug" | "fill";

export interface NavigationRailItemProps {
	selected: boolean;
	icon: React.ReactNode;
	label?: React.ReactNode;
	onClick?: () => void;
	disabled?: boolean;
	badge?: React.ReactNode;
	/** Shape of the item active indicator / icon container (pill, circle, sunny, flower, etc.) */
	shape?: NavigationItemShape;
	/** Override size (width & height) of the shape container in px. Default: 56 for square shapes, 32h×56w for pill */
	shapeSize?: number;
	/**
	 * Width behavior of the active indicator when expanded with pill shape.
	 * - "hug" (default): hugs the icon + label contents (MD3 Expressive style)
	 * - "fill": fills the full width of the container (baseline navigation drawer style)
	 */
	activeIndicatorWidth?: NavigationRailActiveIndicatorWidth;
	"aria-label"?: string;
	className?: string;
	asChild?: boolean;
	children?: React.ReactNode;
}

export interface NavigationRailProps {
	variant?: NavigationRailVariant;
	labelVisibility?: NavigationRailLabelVisibility;
	/** Default shape of navigation rail items (pill, circle, sunny, flower, etc.) */
	shape?: NavigationItemShape;
	/** Default size (width & height) for square shape containers in px. Default: 56 */
	shapeSize?: number;
	/**
	 * Default width behavior of the active indicator when expanded with pill shape.
	 * - "hug" (default): hugs the icon + label contents (MD3 Expressive style)
	 * - "fill": fills the full width of the container (baseline navigation drawer style)
	 */
	activeIndicatorWidth?: NavigationRailActiveIndicatorWidth;
	header?: React.ReactNode;
	fab?: React.ReactNode;
	fabPlacement?: "contained" | "spatialized";
	footer?: React.ReactNode;
	narrow?: boolean;
	open?: boolean;
	onClose?: () => void;
	activeIndicatorTransition?: Transition;
	children: React.ReactNode;
	className?: string;
	style?: React.CSSProperties;
}

const NavigationRailContext = React.createContext<{
	variant: NavigationRailVariant;
	labelVisibility: NavigationRailLabelVisibility;
	shape?: NavigationItemShape;
	shapeSize?: number;
	activeIndicatorWidth?: NavigationRailActiveIndicatorWidth;
	activeIndicatorTransition?: Transition;
}>({
	variant: "collapsed",
	labelVisibility: "labeled",
	activeIndicatorWidth: "hug",
});

const MD3_MODAL_TRANSITION = {
	type: "tween",
	ease: [0.05, 0.7, 0.1, 1],
	duration: 0.3,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// CVA Variants
// ─────────────────────────────────────────────────────────────────────────────

const railContainerVariants = cva(
	"flex flex-col overflow-y-auto overflow-x-hidden select-none transition-colors duration-300",
	{
		variants: {
			variant: {
				collapsed:
					"items-center h-full pt-11 pb-14 shadow-none bg-m3-surface rounded-none",
				expanded:
					"items-start h-full pt-11 pb-14 shadow-none bg-m3-surface rounded-none",
				modal:
					"bg-m3-surface shadow-lg rounded-r-[var(--m3-shape-corner-large)] h-full pt-11 pb-14",
				xr: "h-fit py-5 rounded-[48px] shadow-xl bg-m3-surface border border-white/5",
			},
			narrow: {
				true: "w-20",
				false: "w-24",
			},
		},
		compoundVariants: [
			{ variant: "expanded", className: "min-w-[13.75rem] max-w-[22.5rem]" },
			{ variant: "modal", className: "min-w-[13.75rem] max-w-[22.5rem]" },
		],
		defaultVariants: {
			variant: "collapsed",
			narrow: false,
		},
	},
);

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function cloneIconWithFill(
	icon: React.ReactNode,
	selected: boolean,
): React.ReactNode {
	if (!React.isValidElement(icon)) return icon;
	if ((icon.type as unknown) === Icon) {
		return React.cloneElement(
			icon as React.ReactElement<{ fill?: 0 | 1; animateFill?: boolean }>,
			{ fill: selected ? 1 : 0, animateFill: true },
		);
	}
	return icon;
}

function getMenuItems(container: HTMLElement): HTMLElement[] {
	return Array.from(
		container.querySelectorAll<HTMLElement>(
			'[role="menuitem"]:not([aria-disabled="true"])',
		),
	);
}

function setFocusedItem(items: HTMLElement[], index: number) {
	for (const item of items) item.tabIndex = -1;
	const target = items[index];
	if (target) {
		target.tabIndex = 0;
		target.focus();
	}
}

// ─────────────────────────────────────────────────────────────────────────────
// NavigationRailItem Sub-components
// ─────────────────────────────────────────────────────────────────────────────

interface ActivePillProps {
	layoutId: string;
	disableInitial?: boolean;
	shape?: NavigationItemShape;
	width?: number;
	height?: number;
}

function ActivePill({
	layoutId,
	disableInitial = false,
	shape = "pill",
	width = 56,
	height = 32,
}: ActivePillProps) {
	const { activeIndicatorTransition } = React.useContext(NavigationRailContext);
	const shapeStyle = getNavigationShapeStyle(shape, width, height);

	return (
		<m.div
			layoutId={layoutId}
			className={cn(
				"bg-m3-secondary-container pointer-events-none",
				shapeStyle
					? "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-none"
					: "absolute inset-0 rounded-full",
			)}
			style={{
				zIndex: 0,
				originX: 0.5,
				originY: 0.5,
				...(shapeStyle ? { width, height } : {}),
				...shapeStyle,
			}}
			initial={disableInitial ? false : { opacity: 0, scale: 0.5 }}
			animate={{ opacity: 1, scale: 1 }}
			exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.15 } }}
			transition={activeIndicatorTransition || SPRING_TRANSITION_EXPRESSIVE}
		/>
	);
}

interface HoverStateLayerProps {
	shape?: NavigationItemShape;
	width?: number;
	height?: number;
}

function HoverStateLayer({
	shape = "pill",
	width = 56,
	height = 32,
}: HoverStateLayerProps) {
	const shapeStyle = getNavigationShapeStyle(shape, width, height);

	return (
		<div
			className={cn(
				"bg-m3-on-surface opacity-0 group-hover:opacity-[0.08] transition-opacity duration-200 pointer-events-none z-0",
				shapeStyle
					? "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-none"
					: "absolute inset-0 rounded-full",
			)}
			style={{
				...(shapeStyle ? { width, height } : {}),
				...shapeStyle,
			}}
		/>
	);
}

interface RippleLayerProps {
	ripples: ReturnType<typeof useRippleState>["ripples"];
	onRippleDone: ReturnType<typeof useRippleState>["removeRipple"];
	shape?: NavigationItemShape;
	width?: number;
	height?: number;
}

function RippleLayer({
	ripples,
	onRippleDone,
	shape = "pill",
	width = 56,
	height = 32,
}: RippleLayerProps) {
	const shapeStyle = getNavigationShapeStyle(shape, width, height);

	return (
		<div
			className={cn(
				"overflow-hidden pointer-events-none z-0",
				shapeStyle
					? "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-none"
					: "absolute inset-0 rounded-full",
			)}
			style={{
				...(shapeStyle ? { width, height } : {}),
				...shapeStyle,
			}}
		>
			<Ripple ripples={ripples} onRippleDone={onRippleDone} />
		</div>
	);
}

interface IconContainerProps {
	selected: boolean;
	badge?: React.ReactNode;
	children: React.ReactNode;
}

function IconContainer({ selected, badge, children }: IconContainerProps) {
	return (
		<div
			aria-hidden="true"
			className={cn(
				"relative flex items-center justify-center size-6 transition-colors duration-200",
				selected
					? "text-m3-on-secondary-container"
					: "text-m3-on-surface-variant",
			)}
		>
			{children}
			{badge && (
				<span className="absolute -top-1 -right-1 flex min-w-3 h-3 items-center justify-center rounded-full bg-m3-error px-1 text-[10px] font-medium leading-none tracking-normal text-m3-on-error ring-[1.5px] ring-m3-surface">
					{badge}
				</span>
			)}
		</div>
	);
}

// ─────────────────────────────────────────────────────────────────────────────
// NavigationRailItem
// ─────────────────────────────────────────────────────────────────────────────

const NavigationRailItemComponent = React.forwardRef<
	HTMLButtonElement,
	NavigationRailItemProps
>(
	(
		{
			selected,
			icon,
			label,
			onClick,
			disabled = false,
			badge,
			shape: shapeProp,
			shapeSize: shapeSizeProp,
			activeIndicatorWidth: activeIndicatorWidthProp,
			className,
			"aria-label": ariaLabelProp,
			asChild = false,
			children,
		},
		ref,
	) => {
		const {
			variant,
			labelVisibility,
			shape: contextShape,
			shapeSize: contextShapeSize,
			activeIndicatorWidth: contextActiveIndicatorWidth,
		} = React.useContext(NavigationRailContext);

		const activeShape = shapeProp ?? contextShape ?? "pill";
		const isSquare = isSquareShape(activeShape);
		// shapeSize controls the square dimension of custom shape containers (default 56px)
		const resolvedShapeSize = shapeSizeProp ?? contextShapeSize ?? 56;
		const activeIndicatorWidth =
			activeIndicatorWidthProp ?? contextActiveIndicatorWidth ?? "hug";

		const isExpanded = variant === "expanded" || variant === "modal";
		const isModal = variant === "modal";
		const enableLayout = !isModal;
		const isExpandedPill = isExpanded && !isSquare;
		const expandedPillWidthClass =
			activeIndicatorWidth === "fill" ? "w-full" : "w-fit";

		const activePillId = `rail-pill-${React.useId()}`;
		const { ripples, onPointerDown, removeRipple } = useRippleState({
			disabled,
		});

		const showLabel =
			isExpanded ||
			labelVisibility === "labeled" ||
			(labelVisibility === "auto" && selected);

		const handleClick = React.useCallback(
			(e: React.MouseEvent<HTMLButtonElement>) => {
				if (disabled) {
					e.preventDefault();
					return;
				}
				onClick?.();
			},
			[disabled, onClick],
		);

		const filledIcon = cloneIconWithFill(icon, selected);

		const labelInitial = isModal
			? false
			: { opacity: 0, x: isExpanded ? -12 : 0, y: isExpanded ? 0 : -8 };

		const itemClassName = cn(
			"group relative flex cursor-pointer transition-colors duration-200 outline-none select-none",
			"focus-visible:ring-2 focus-visible:ring-m3-primary focus-visible:ring-offset-2 rounded-full",
			disabled && "pointer-events-none opacity-[0.38]",
			isExpanded
				? "w-full flex-row items-center px-3 h-14"
				: "w-full flex-col justify-center h-14",
			className,
		);

		const iconPillWidth = isSquare ? resolvedShapeSize : 56;
		const iconPillHeight = isSquare ? resolvedShapeSize : 32;

		if (asChild && children) {
			const child = React.Children.only(children) as React.ReactElement<{
				children?: React.ReactNode;
			}>;

			const innerContent = (
				<>
					{/* Pill container - adapts layout for expanded vs collapsed */}
					<m.div
						layout={enableLayout}
						className={cn(
							"relative flex z-10",
							isExpanded
								? cn(
										"flex-row items-center h-14 px-4 gap-x-3 rounded-full",
										expandedPillWidthClass,
									)
								: "flex-col items-center justify-center w-full gap-y-1 rounded-full",
						)}
					>
						{isExpandedPill && (
							<>
								<AnimatePresence initial={false}>
									{selected && (
										<ActivePill
											layoutId={activePillId}
											disableInitial={isModal}
											shape={activeShape}
										/>
									)}
								</AnimatePresence>
								<HoverStateLayer shape={activeShape} />
								<RippleLayer
									ripples={ripples}
									onRippleDone={removeRipple}
									shape={activeShape}
								/>
							</>
						)}

						{/* Icon pill container */}
						<m.div
							layout={enableLayout}
							className={cn(
								"relative flex items-center justify-center shrink-0 z-10",
								isSquare
									? "mx-auto"
									: isExpandedPill
										? "size-6"
										: "h-8 w-14 mx-auto rounded-full",
							)}
							style={
								isSquare
									? { width: resolvedShapeSize, height: resolvedShapeSize }
									: undefined
							}
						>
							{!isExpandedPill && (
								<>
									<AnimatePresence initial={false}>
										{selected && (
											<ActivePill
												layoutId={activePillId}
												disableInitial={isModal}
												shape={activeShape}
												width={iconPillWidth}
												height={iconPillHeight}
											/>
										)}
									</AnimatePresence>
									<HoverStateLayer
										shape={activeShape}
										width={iconPillWidth}
										height={iconPillHeight}
									/>
									<RippleLayer
										ripples={ripples}
										onRippleDone={removeRipple}
										shape={activeShape}
										width={iconPillWidth}
										height={iconPillHeight}
									/>
								</>
							)}

							<m.div
								layout={enableLayout ? "position" : false}
								className="relative z-10 flex size-6 items-center justify-center text-current"
							>
								<IconContainer selected={selected} badge={badge}>
									{filledIcon}
								</IconContainer>
							</m.div>
						</m.div>

						<AnimatePresence mode="popLayout">
							{showLabel && (label || child.props.children) && (
								<m.span
									key="rail-label"
									layout={enableLayout ? "position" : false}
									initial={labelInitial}
									animate={{ opacity: 1, x: 0, y: 0 }}
									exit={{ opacity: 0, transition: { duration: 0.1 } }}
									transition={SPRING_TRANSITION}
									className={cn(
										"z-10 transition-colors duration-200 whitespace-nowrap",
										selected
											? isExpandedPill
												? "text-m3-on-secondary-container font-medium"
												: "text-m3-on-surface font-medium"
											: "text-m3-on-surface-variant font-medium",
										isExpanded
											? "text-sm tracking-wide text-left"
											: "text-xs tracking-wide",
									)}
								>
									{child.props.children ?? label}
								</m.span>
							)}
						</AnimatePresence>
					</m.div>

					<TouchTarget />
				</>
			);

			return (
				<LazyMotion features={domMax} strict>
					<Slot
						ref={ref as React.Ref<HTMLElement>}
						role="menuitem"
						aria-current={selected ? "page" : undefined}
						aria-disabled={disabled ? true : undefined}
						aria-label={
							ariaLabelProp || (typeof label === "string" ? label : undefined)
						}
						onClick={handleClick}
						onPointerDown={onPointerDown}
						className={itemClassName}
						tabIndex={-1}
					>
						{React.cloneElement(child, { children: innerContent })}
					</Slot>
				</LazyMotion>
			);
		}

		return (
			<LazyMotion features={domMax} strict>
				<m.button
					layout={enableLayout}
					ref={ref}
					type="button"
					role="menuitem"
					aria-current={selected ? "page" : undefined}
					aria-disabled={disabled ? true : undefined}
					aria-label={
						ariaLabelProp || (typeof label === "string" ? label : undefined)
					}
					onClick={handleClick}
					onPointerDown={onPointerDown}
					className={itemClassName}
					tabIndex={-1}
				>
					{/* Pill container - adapts layout for expanded vs collapsed */}
					<m.div
						layout={enableLayout}
						className={cn(
							"relative flex z-10",
							isExpanded
								? cn(
										"flex-row items-center h-14 px-4 gap-x-3 rounded-full",
										expandedPillWidthClass,
									)
								: "flex-col items-center justify-center w-full gap-y-1 rounded-full",
						)}
					>
						{isExpandedPill && (
							<>
								<AnimatePresence initial={false}>
									{selected && (
										<ActivePill
											layoutId={activePillId}
											disableInitial={isModal}
											shape={activeShape}
										/>
									)}
								</AnimatePresence>
								<HoverStateLayer shape={activeShape} />
								<RippleLayer
									ripples={ripples}
									onRippleDone={removeRipple}
									shape={activeShape}
								/>
							</>
						)}

						{/* Icon pill container */}
						<m.div
							layout={enableLayout}
							className={cn(
								"relative flex items-center justify-center shrink-0 z-10",
								isSquare
									? "mx-auto"
									: isExpandedPill
										? "size-6"
										: "h-8 w-14 mx-auto rounded-full",
							)}
							style={
								isSquare
									? { width: resolvedShapeSize, height: resolvedShapeSize }
									: undefined
							}
						>
							{!isExpandedPill && (
								<>
									<AnimatePresence initial={false}>
										{selected && (
											<ActivePill
												layoutId={activePillId}
												disableInitial={isModal}
												shape={activeShape}
												width={iconPillWidth}
												height={iconPillHeight}
											/>
										)}
									</AnimatePresence>
									<HoverStateLayer
										shape={activeShape}
										width={iconPillWidth}
										height={iconPillHeight}
									/>
									<RippleLayer
										ripples={ripples}
										onRippleDone={removeRipple}
										shape={activeShape}
										width={iconPillWidth}
										height={iconPillHeight}
									/>
								</>
							)}

							<m.div
								layout={enableLayout ? "position" : false}
								className="relative z-10 flex size-6 items-center justify-center text-current"
							>
								<IconContainer selected={selected} badge={badge}>
									{filledIcon}
								</IconContainer>
							</m.div>
						</m.div>

						<AnimatePresence mode="popLayout">
							{showLabel && label && (
								<m.span
									key="rail-label"
									layout={enableLayout ? "position" : false}
									initial={labelInitial}
									animate={{ opacity: 1, x: 0, y: 0 }}
									exit={{ opacity: 0, transition: { duration: 0.1 } }}
									transition={SPRING_TRANSITION}
									className={cn(
										"z-10 transition-colors duration-200 whitespace-nowrap",
										selected
											? isExpandedPill
												? "text-m3-on-secondary-container font-medium"
												: "text-m3-on-surface font-medium"
											: "text-m3-on-surface-variant font-medium",
										isExpanded
											? "text-sm tracking-wide text-left"
											: "text-xs tracking-wide",
									)}
								>
									{label}
								</m.span>
							)}
						</AnimatePresence>
					</m.div>

					<TouchTarget />
				</m.button>
			</LazyMotion>
		);
	},
);

NavigationRailItemComponent.displayName = "NavigationRailItem";
export const NavigationRailItem = React.memo(NavigationRailItemComponent);

// ─────────────────────────────────────────────────────────────────────────────
// NavigationRail Container
// ─────────────────────────────────────────────────────────────────────────────

function useRoving(navRef: React.RefObject<HTMLElement | null>) {
	React.useEffect(() => {
		if (!navRef.current) return;
		const items = getMenuItems(navRef.current);
		const selected = items.find(
			(el) => el.getAttribute("aria-current") === "page",
		);

		for (const item of items) item.tabIndex = -1;
		const firstFocusable = selected ?? items[0];
		if (firstFocusable) firstFocusable.tabIndex = 0;
	}, [navRef]);

	return React.useCallback(
		(e: React.KeyboardEvent<HTMLElement>) => {
			if (!navRef.current) return;
			const items = getMenuItems(navRef.current);
			if (items.length === 0) return;

			const currentIndex = items.indexOf(document.activeElement as HTMLElement);

			const keyMap: Record<string, () => number> = {
				ArrowDown: () =>
					currentIndex < items.length - 1 ? currentIndex + 1 : 0,
				ArrowRight: () =>
					currentIndex < items.length - 1 ? currentIndex + 1 : 0,
				ArrowUp: () => (currentIndex > 0 ? currentIndex - 1 : items.length - 1),
				ArrowLeft: () =>
					currentIndex > 0 ? currentIndex - 1 : items.length - 1,
				Home: () => 0,
				End: () => items.length - 1,
			};

			const getNextIndex = keyMap[e.key];

			if (getNextIndex) {
				e.preventDefault();
				setFocusedItem(items, getNextIndex());
				return;
			}

			if (
				(e.key === " " || e.key === "Enter") &&
				items.includes(document.activeElement as HTMLElement)
			) {
				e.preventDefault();
				(document.activeElement as HTMLElement).click();
			}
		},
		[navRef],
	);
}

const NavigationRailComponent = React.forwardRef<
	HTMLElement,
	NavigationRailProps
>(
	(
		{
			variant = "collapsed",
			labelVisibility = "labeled",
			shape,
			shapeSize,
			activeIndicatorWidth = "hug",
			header,
			fab,
			fabPlacement = "contained",
			footer,
			narrow = false,
			open = false,
			onClose,
			activeIndicatorTransition,
			children,
			className,
			style,
		},
		ref,
	) => {
		const isModal = variant === "modal";
		const isExpanded = variant === "expanded" || variant === "modal";
		const isXr = variant === "xr";
		const isSpatial = isXr && fabPlacement === "spatialized";
		const applyAnimation = !isXr || !isSpatial;

		const navRef = React.useRef<HTMLElement>(null);
		const handleKeyDown = useRoving(navRef);

		const setRefs = React.useCallback(
			(node: HTMLElement | null) => {
				navRef.current = node;
				if (typeof ref === "function") ref(node);
				else if (ref) ref.current = node;
			},
			[ref],
		);

		const navBaseClasses = cn(railContainerVariants({ variant, narrow }));
		const modalPositioning = isModal ? "fixed left-0 top-0 z-[100]" : "";

		const navHeaderSpacing = (() => {
			if (!isXr) return "mb-6 min-h-10";
			if (fabPlacement === "contained") return fab ? "mb-10" : "mb-5";
			return "mb-5";
		})();

		const navElement = (
			<m.nav
				key="md3-nav-rail"
				layout={!isModal}
				ref={isSpatial ? undefined : setRefs}
				role="navigation"
				aria-label="Main navigation"
				className={cn(
					navBaseClasses,
					!isSpatial && modalPositioning,
					!isSpatial && isModal && applyAnimation && "will-change-transform",
					!isSpatial && className,
				)}
				style={isSpatial ? undefined : style}
				onKeyDown={handleKeyDown}
				initial={isModal && applyAnimation ? { x: "-100%" } : false}
				animate={isModal && applyAnimation ? { x: 0 } : false}
				exit={isModal && applyAnimation ? { x: "-100%" } : undefined}
				transition={isModal ? MD3_MODAL_TRANSITION : SPRING_TRANSITION}
			>
				{(header || (fab && !isSpatial)) && (
					<m.div
						layout
						className={cn(
							"flex w-full flex-col justify-start shrink-0 empty:hidden",
							isExpanded ? "items-start px-3" : "items-center",
							navHeaderSpacing,
						)}
					>
						{header}
						{header && fab && !isSpatial && (
							<div className={isXr ? "h-1" : "h-4"} />
						)}
						{!isSpatial && fab}
					</m.div>
				)}

				<div
					role="menubar"
					aria-orientation="vertical"
					className="flex flex-col flex-1 w-full gap-y-5"
				>
					{children}
				</div>

				{footer && (
					<div className="flex w-full flex-col items-center justify-end mt-auto shrink-0 pt-4 empty:hidden">
						{footer}
					</div>
				)}
			</m.nav>
		);

		const spatialFabSize = narrow
			? "size-20 rounded-[40px]"
			: "size-24 rounded-[48px]";

		const spatialWrapper = (
			<m.div
				key="md3-nav-wrapper"
				ref={setRefs}
				className={cn(
					"flex flex-col items-center gap-y-5 pointer-events-none",
					modalPositioning,
					"m-6",
					isModal && "will-change-transform",
					className,
				)}
				style={style}
				initial={isModal ? { x: "-100%" } : false}
				animate={isModal ? { x: 0 } : false}
				exit={isModal ? { x: "-100%" } : undefined}
				transition={isModal ? MD3_MODAL_TRANSITION : SPRING_TRANSITION}
			>
				{fab && (
					<div
						className={cn(
							"flex shrink-0 items-center justify-center pointer-events-auto",
							spatialFabSize,
						)}
					>
						{fab}
					</div>
				)}
				{React.cloneElement(
					navElement as React.ReactElement<{ className?: string }>,
					{
						className: cn(navBaseClasses, "pointer-events-auto"),
					},
				)}
			</m.div>
		);

		const finalNavElement = isSpatial ? spatialWrapper : navElement;

		const contextValue = {
			variant,
			labelVisibility,
			shape,
			shapeSize,
			activeIndicatorWidth,
			activeIndicatorTransition,
		};

		if (isModal) {
			if (typeof document === "undefined") return null;

			return createPortal(
				<LazyMotion features={domMax} strict>
					<NavigationRailContext.Provider value={contextValue}>
						<AnimatePresence>
							{open && (
								<m.div
									key="md3-nav-backdrop"
									initial={{ opacity: 0 }}
									animate={{ opacity: 1 }}
									exit={{ opacity: 0 }}
									transition={{ duration: 0.2, ease: "linear" }}
									className="fixed inset-0 bg-black/40 z-40 will-change-[opacity]"
									onClick={onClose}
									aria-hidden="true"
								/>
							)}
							{open && finalNavElement}
						</AnimatePresence>
					</NavigationRailContext.Provider>
				</LazyMotion>,
				document.body,
			);
		}

		return (
			<LazyMotion features={domMax} strict>
				<NavigationRailContext.Provider value={contextValue}>
					{finalNavElement}
				</NavigationRailContext.Provider>
			</LazyMotion>
		);
	},
);

NavigationRailComponent.displayName = "NavigationRail";
export const NavigationRail = React.memo(NavigationRailComponent);
